import React, { useState, useEffect } from 'react'
import PropTypes from 'prop-types'
import styled from 'styled-components/macro'
import { useTranslation } from 'react-i18next'
import FilterCollapser from 'components/molecules/filter-collapser'
import Checkbox from 'components/atoms/checkbox'
import TextLink from 'components/atoms/text-link'
import ToggleSwitch from 'components/atoms/toggle-switch'
import { toKebabCase } from 'utilities/format'
import { PortalsToggleFieldEnum } from 'config/enums'

const StyledCheckbox = styled(Checkbox)`
  margin-top: ${({ theme }) => theme.sizings.lvl2};
`

const StyledTextLink = styled(TextLink)`
  margin-top: ${({ theme }) => theme.sizings.lvl1};
`

const StyledToggleSwitch = styled(ToggleSwitch)`
  .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track {
    background-color: ${({ theme }) => theme.colors.statusComplete};
    opacity: 1;
  }

  .MuiSwitch-track {
    background-color: ${({ theme }) => theme.colors.inactiveBackground};
    opacity: 1;
  }

  .MuiSwitch-thumb {
    background-color: ${({ theme }) => theme.colors.defaultBackground};
  }
`

function AdvertisementPortalFilters({
  name,
  filter,
  filterOptions,
  initallyOpen,
  onFilterUpdate,
  selectedFilters,
  title,
  toggleValue,
}) {
  const { t } = useTranslation()
  const [showHiddenValues, setShowHiddenValues] = useState(false)
  const maxOptions = 8

  const hideOptions = maxOptions + 5 < filterOptions.length
  const visibleOptions =
    hideOptions && !showHiddenValues
      ? filterOptions.slice(0, maxOptions)
      : filterOptions

  const toggleText = !showHiddenValues
    ? t('showMoreFilterValues')
    : t('showLessFilterValues')

  const normalizedSelectedFilters = selectedFilters.map(String)

  useEffect(() => {
    const initialSelected = filterOptions
      .filter((option) => option.selected)
      .map((option) => option.value)

    if (initialSelected.length > 0 && selectedFilters.length === 0) {
      onFilterUpdate(name, initialSelected)
    }
  }, [filterOptions, name, onFilterUpdate, selectedFilters])

  const toggleHidden = () => {
    setShowHiddenValues((prev) => !prev)
  }

  const handleChange = (value, checked) => {
    const uniqueValues = new Set(selectedFilters.map(String))

    if (checked) {
      uniqueValues.add(String(value))
    } else {
      uniqueValues.delete(String(value))
    }

    const typedValues = Array.from(uniqueValues).map((val) =>
      /^\d+$/.test(val) ? Number(val) : val,
    )

    onFilterUpdate(name, typedValues)
  }

  const handleToggleSwitchChange = (isChecked) => {
    const toggleField = filter?.toggle_field
    if (toggleField) {
      onFilterUpdate(
        toggleField,
        isChecked ? PortalsToggleFieldEnum.ON : PortalsToggleFieldEnum.OFF,
      )
    }
  }

  return (
    <FilterCollapser initallyOpen={initallyOpen} title={t(title)}>
      <StyledToggleSwitch
        labelLeft={t('niet')}
        labelRight={t('wel')}
        checked={toggleValue === '1'}
        onChange={handleToggleSwitchChange}
      />
      {visibleOptions.map((option, index) => (
        <StyledCheckbox
          key={index.toString()}
          checked={normalizedSelectedFilters.includes(String(option.value))}
          id={`${name}_${option.value}`}
          name={`${name}_${option.value}`}
          onChange={(checked) => handleChange(option.value, checked)}
          title={t(option.label)}
          value={String(option.value)}
          data-test-e2e={toKebabCase(`filter-option-${name}-${option.value}`)}
        />
      ))}
      {hideOptions && (
        <StyledTextLink onClick={toggleHidden} text={toggleText} />
      )}
    </FilterCollapser>
  )
}

AdvertisementPortalFilters.propTypes = {
  name: PropTypes.string.isRequired,
  filter: PropTypes.object.isRequired,
  filterOptions: PropTypes.array.isRequired,
  initallyOpen: PropTypes.bool,
  onFilterUpdate: PropTypes.func.isRequired,
  selectedFilters: PropTypes.array,
  title: PropTypes.string.isRequired,
  toggleValue: PropTypes.string,
}

AdvertisementPortalFilters.defaultProps = {
  initallyOpen: null,
  selectedFilters: [],
  toggleValue: '0',
}

export default AdvertisementPortalFilters
